import { useEffect, useState } from "react";
import { Close } from "../../Icons/Icons";
import ToggleSwitch from "../ToggleSwitch/ToggleSwitch";
import { useDispatch, useSelector } from 'react-redux';
import { getEvents, requestsData } from "../../reducer/dataSlice";
import { getCookie } from "../../reducer/utils";

const CreateFutureOriginal = ({ showFilter, setShowFilter, notify, getFutureOriginal }) => {

    const [isSeries, setIsSeries] = useState(false);


    const defaultFormData = {
        content_name: '', // required
        description: '', // required
        is_series: false, // required
        watch_url: null, // required
        upcoming_season_number: null, // required if is_series is true
        platform_season_number: null, // not required
        episodes_count: null, // not required
        provider: null, // required
        language_code: '', // required
        future_original_country_mapping: [
            {
                country_code: '', // required
                release_date_time: null, // required for non-series
                episodes_release_date_time: [] // required for series
            }
        ],
        source_details: [], // not required
        imdb_id: '', // not required
        tmdb_id: '', // not required
        poster_path: '', // not required
    }

    const [formData, setFormData] = useState(defaultFormData);

    const [errors, setErrors] = useState({});

    const { loading } = useSelector(requestsData);

    const validateForm = () => {
        const errors = {};

        if (!formData.content_name.trim()) {
            errors.content_name = "Content Name is required";
        }
        if (!formData.description.trim()) {
            errors.description = "Description is required";
        }
        if (!formData.watch_url?.trim()) {
            errors.watch_url = "Watch URL is required";
        }
        if (!formData.provider.trim() || formData.provider === "Select") {
            errors.provider = "Provider is required";
        }
        if (formData.language_code.length === 0) {
            errors.language_code = "At least one language code is required";
        }

        // Validate each country mapping
        formData.future_original_country_mapping.forEach((mapping, index) => {
            if (!mapping.country_code) {
                errors[`country_code_${index}`] = "Country code is required";
            }

            if (formData.is_series) {
                if (!mapping.episodes_release_date_time || mapping.episodes_release_date_time.length === 0) {
                    errors[`episodes_release_date_time_${index}`] = "Episode release date is required for series";
                }
            } else {
                if (!mapping.release_date_time) {
                    errors[`release_date_time_${index}`] = "Release date is required for non-series";
                }
            }
        });

        if (formData.is_series && !formData.upcoming_season_number) {
            errors.upcoming_season_number = "Season Number is required for series";
        }

        return errors;
    };

    const handleListInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value.split(',').map(item => item.trim())
        });
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateForm();
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            console.error("Validation errors:", validationErrors);
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_BASE_URL}/internal/api/v4/vod/future-originals`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${getCookie("token")}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    ...formData,
                    platform_season_number: formData.platform_season_number
                        ? Number(formData.platform_season_number)
                        : null,
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { status: response.status, message: errorData.message || 'Something went wrong' };
            }

            notify('Future Original Created Successfully!');
            setShowFilter(false);
            getFutureOriginal();
            setFormData(defaultFormData);
        } catch (err) {
            notify(err.message || 'Failed to create Future Original');
            console.error("Error:", err);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };


    const handleChangeCheckbox = (e) => {
        const isSeriesValue = e.target.checked;

        // Update mapping fields based on is_series value
        const updatedMappings = formData.future_original_country_mapping.map(mapping => {
            if (isSeriesValue) {
                // If changing to series, initialize episodes_release_date_time if empty
                return {
                    ...mapping,
                    episodes_release_date_time: mapping.episodes_release_date_time.length ?
                        mapping.episodes_release_date_time :
                        [mapping.release_date_time || null]
                };
            } else {
                // If changing to non-series, use first episode date as release_date_time if available
                return {
                    ...mapping,
                    release_date_time: mapping.release_date_time ||
                        (mapping.episodes_release_date_time.length ? mapping.episodes_release_date_time[0] : null)
                };
            }
        });

        setFormData({
            ...formData,
            [e.target.name]: isSeriesValue,
            future_original_country_mapping: updatedMappings
        });
    };

    const handleCountryMappingChange = (index, field, value) => {
        const updatedMappings = [...formData.future_original_country_mapping];
        updatedMappings[index] = {
            ...updatedMappings[index],
            [field]: value
        };

        setFormData({
            ...formData,
            future_original_country_mapping: updatedMappings
        });
    };

    const addCountryMapping = () => {
        setFormData({
            ...formData,
            future_original_country_mapping: [
                ...formData.future_original_country_mapping,
                {
                    country_code: '',
                    release_date_time: null,
                    episodes_release_date_time: []
                }
            ]
        });
    };

    const removeCountryMapping = (index) => {
        if (formData.future_original_country_mapping.length <= 1) {
            notify('At least one country mapping is required');
            return;
        }

        const updatedMappings = [...formData.future_original_country_mapping];
        updatedMappings.splice(index, 1);

        setFormData({
            ...formData,
            future_original_country_mapping: updatedMappings
        });
    };

    const duplicateCountryMapping = (index) => {
        const mappingToDuplicate = formData.future_original_country_mapping[index];
        const duplicatedMapping = {
            ...mappingToDuplicate,
            country_code: '' // Clear the country code for the duplicate
        };

        const updatedMappings = [...formData.future_original_country_mapping];
        updatedMappings.splice(index + 1, 0, duplicatedMapping);

        setFormData({
            ...formData,
            future_original_country_mapping: updatedMappings
        });
    };

    const addEpisodeReleaseDate = (countryIndex) => {
        const updatedMappings = [...formData.future_original_country_mapping];
        updatedMappings[countryIndex] = {
            ...updatedMappings[countryIndex],
            episodes_release_date_time: [
                ...updatedMappings[countryIndex].episodes_release_date_time,
                null
            ]
        };

        setFormData({
            ...formData,
            future_original_country_mapping: updatedMappings
        });
    };

    const removeEpisodeReleaseDate = (countryIndex, episodeIndex) => {
        if (formData.future_original_country_mapping[countryIndex].episodes_release_date_time.length <= 1) {
            notify('At least one episode release date is required');
            return;
        }

        const updatedMappings = [...formData.future_original_country_mapping];
        const updatedEpisodeDates = [...updatedMappings[countryIndex].episodes_release_date_time];
        updatedEpisodeDates.splice(episodeIndex, 1);

        updatedMappings[countryIndex] = {
            ...updatedMappings[countryIndex],
            episodes_release_date_time: updatedEpisodeDates
        };

        setFormData({
            ...formData,
            future_original_country_mapping: updatedMappings
        });
    };

    const handleEpisodeDateChange = (countryIndex, episodeIndex, value) => {
        const updatedMappings = [...formData.future_original_country_mapping];
        const updatedEpisodeDates = [...updatedMappings[countryIndex].episodes_release_date_time];
        updatedEpisodeDates[episodeIndex] = value;

        updatedMappings[countryIndex] = {
            ...updatedMappings[countryIndex],
            episodes_release_date_time: updatedEpisodeDates
        };

        setFormData({
            ...formData,
            future_original_country_mapping: updatedMappings
        });
    };

    const handleDragStart = (e, countryIndex, episodeIndex) => {
        e.dataTransfer.setData('text/plain', JSON.stringify({ countryIndex, episodeIndex }));
        e.target.classList.add('opacity-50');
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.currentTarget.classList.add('border-primary1');
    };

    const handleDragLeave = (e) => {
        e.currentTarget.classList.remove('border-primary1');
    };

    const handleDrop = (e, targetCountryIndex, targetEpisodeIndex) => {
        e.preventDefault();
        e.currentTarget.classList.remove('border-primary1');

        const data = JSON.parse(e.dataTransfer.getData('text/plain'));
        const { countryIndex: sourceCountryIndex, episodeIndex: sourceEpisodeIndex } = data;

        // Only allow reordering within the same country
        if (sourceCountryIndex !== targetCountryIndex) return;

        const updatedMappings = [...formData.future_original_country_mapping];
        const episodeDates = [...updatedMappings[sourceCountryIndex].episodes_release_date_time];

        // Remove the episode from its original position
        const [movedEpisode] = episodeDates.splice(sourceEpisodeIndex, 1);
        // Insert it at the new position
        episodeDates.splice(targetEpisodeIndex, 0, movedEpisode);

        updatedMappings[sourceCountryIndex] = {
            ...updatedMappings[sourceCountryIndex],
            episodes_release_date_time: episodeDates
        };

        setFormData({
            ...formData,
            future_original_country_mapping: updatedMappings
        });
    };

    const handleDragEnd = (e) => {
        e.target.classList.remove('opacity-50');
    };

    return (
        <div className={`fixed top-0 left-0 w-screen h-screen z-50 ${showFilter ? 'visible' : 'hidden'}`}>
            <div className="absolute bg-gray-800 bg-opacity-25 backdrop-blur w-full h-full" onClick={() => setShowFilter(false)}></div>
            <div className={`max-w-md w-full h-screen mx-auto bg-white rounded-lg shadow-lg absolute ${showFilter ? 'right-0' : '-right-full'} top-0`}>
                <div className="flex items-center justify-between p-5 border-b">
                    <h2 className="text-lg text-inherit font-semibold">Create Request</h2>
                    <Close className='cursor-pointer' onClick={() => setShowFilter(false)} />
                </div>
                <form className="p-5 flex flex-col justify-between" style={{ height: 'calc(100vh - 70px)' }} onSubmit={handleSubmit}>
                    <div className="max-h-full overflow-auto">
                        <div className="mb-2.5">
                            <label className="block text-gray-900 font-medium text-sm mb-2">Content Name</label>
                            <input
                                type="text"
                                placeholder="Content Name"
                                className="block w-full border-gray-300 rounded-md shadow-sm px-4 py-3 bg-gray-50"
                                value={formData.content_name}
                                name="content_name"
                                onChange={handleChange}
                            />
                            {errors.content_name && <p className="text-red-600 text-sm">{errors.content_name}</p>}
                        </div>
                        <div className="mb-2.5">
                            <label className="block text-gray-900 font-medium text-sm mb-2">Description</label>
                            <textarea
                                placeholder="Description"
                                className="block w-full border-gray-300 rounded-md shadow-sm px-4 py-3 bg-gray-50"
                                value={formData.description}
                                name="description"
                                onChange={handleChange}
                            />
                            {errors.description && <p className="text-red-600 text-sm">{errors.description}</p>}
                        </div>
                        <div className="mb-2.5">
                            <label className="block text-gray-900 font-medium text-sm mb-2">Is Series</label>
                            <ToggleSwitch name="is_series" value={formData.is_series} handleChange={handleChangeCheckbox} />
                        </div>

                        <div className="mb-2.5">
                            <label className="block text-gray-900 font-medium text-sm mb-2">Watch URL</label>
                            <input
                                type="url"
                                placeholder="https://example.com"
                                className="block w-full border-gray-300 rounded-md shadow-sm px-4 py-3 bg-gray-50"
                                value={formData.watch_url || ''}
                                name="watch_url"
                                onChange={handleChange}
                            />
                            {errors.watch_url && <p className="text-red-600 text-sm">{errors.watch_url}</p>}
                        </div>

                        <div className="mb-2.5">
                            <label className="block text-gray-900 font-medium text-sm mb-2">Poster Path</label>
                            <input
                                type="url"
                                placeholder="https://example.com"
                                className="block w-full border-gray-300 rounded-md shadow-sm px-4 py-3 bg-gray-50"
                                value={formData.poster_path || ''}
                                name="poster_path"
                                onChange={handleChange}
                            />
                            {errors.poster_path && <p className="text-red-600 text-sm">{errors.poster_path}</p>}
                        </div>

                        {formData.is_series && (
                            <>
                                <div className="mb-2.5">
                                    <label className="block text-gray-900 font-medium text-sm mb-2">Upcoming Season Number</label>
                                    <input
                                        type="number"
                                        placeholder="1"
                                        className="block w-full border-gray-300 rounded-md shadow-sm px-4 py-3 bg-gray-50"
                                        value={formData.upcoming_season_number}
                                        onChange={handleChange}
                                        name="upcoming_season_number"
                                    />
                                    {errors.upcoming_season_number && <p className="text-red-600 text-sm">{errors.upcoming_season_number}</p>}
                                </div>
                                <div className="mb-2.5">
                                    <label className="block text-gray-900 font-medium text-sm mb-2">Platform Season Number</label>
                                    <input
                                        type="number"
                                        placeholder="1"
                                        className="block w-full border-gray-300 rounded-md shadow-sm px-4 py-3 bg-gray-50"
                                        value={formData.platform_season_number ?? ""}
                                        onChange={handleChange}
                                        name="platform_season_number"
                                    />
                                </div>
                                <div className="mb-2.5">
                                    <label className="block text-gray-900 font-medium text-sm mb-2">Episode Count</label>
                                    <input
                                        type="number"
                                        placeholder="10"
                                        className="block w-full border-gray-300 rounded-md shadow-sm px-4 py-3 bg-gray-50"
                                        value={formData.episodes_count}
                                        onChange={handleChange}
                                        name="episodes_count"
                                    />
                                </div>
                            </>
                        )}
                        <div className="mb-2.5">
                            <label className="block text-gray-900 font-medium text-sm mb-2">Provider</label>
                            <select id="client-request-id" className="mt-1 block w-full border-gray-300 rounded-md shadow-sm px-4 py-3 bg-gray-50"
                                value={formData.provider}
                                onChange={handleChange}
                                name="provider"
                            >
                                <option>Select</option>
                                <option value="netflix">netflix</option>
                                <option value="youtube_tv">youtube_tv</option>
                                <option value="max">max</option>
                                <option value="paramount_plus">paramount_plus</option>
                                <option value="starz">starz</option>
                                <option value="cinemax">cinemax</option>
                                <option value="mgm_plus">mgm_plus</option>
                                <option value="hallmark">hallmark</option>
                                <option value="hbo">hbo</option>
                                <option value="amc_plus">amc_plus</option>
                                <option value="hulu">hulu</option>
                                <option value="disney">disney</option>
                                <option value="prime">prime</option>
                                <option value="apple_tv">apple_tv</option>
                                <option value="peacocktv">peacocktv</option>
                                <option value="discovery_plus">discovery_plus</option>
                                <option value="britbox">britbox</option>
                                <option value="fx">fx</option>
                                <option value="showtime">showtime</option>
                            </select>
                            {errors.provider && <p className="text-red-600 text-sm">{errors.provider}</p>}
                        </div>


                        <div className="mb-2.5">
                            <label className="block text-gray-900 font-medium text-sm mb-2">IMDB ID</label>
                            <input
                                type="text"
                                placeholder="XXX"
                                className="block w-full border-gray-300 rounded-md shadow-sm px-4 py-3 bg-gray-50"
                                value={formData.imdb_id || ''}
                                name="imdb_id"
                                onChange={handleChange}
                            />
                            {errors.imdb_id && <p className="text-red-600 text-sm">{errors.imdb_id}</p>}
                        </div>

                        <div className="mb-2.5">
                            <label className="block text-gray-900 font-medium text-sm mb-2">TMDB ID</label>
                            <input
                                type="text"
                                placeholder="XXX"
                                className="block w-full border-gray-300 rounded-md shadow-sm px-4 py-3 bg-gray-50"
                                value={formData.tmdb_id || ''}
                                name="tmdb_id"
                                onChange={handleChange}
                            />
                            {errors.tmdb_id && <p className="text-red-600 text-sm">{errors.tmdb_id}</p>}
                        </div>



                        <div className="mb-2.5">
                            <label className="block text-gray-900 font-medium text-sm mb-2">Language Code</label>
                            <input
                                type="text"
                                placeholder="en"
                                className="block w-full border-gray-300 rounded-md shadow-sm px-4 py-3 bg-gray-50"
                                value={formData.language_code}
                                name="language_code"
                                onChange={handleChange}
                            />
                            {errors.language_code && <p className="text-red-600 text-sm">{errors.language_code}</p>}
                        </div>

                        <div className="mb-2.5">
                            <label className="block text-gray-900 font-medium text-sm mb-2">Source Details (comma separated)</label>
                            <input
                                type="text"
                                placeholder="Source Details (comma separated)"
                                className="block w-full border-gray-300 rounded-md shadow-sm px-4 py-3 bg-gray-50"
                                // format the array to string with comma separated values with space after comma
                                value={formData.source_details.join(', ')}
                                name="source_details"
                                onChange={handleListInputChange}
                            />
                            {errors.source_details && <p className="text-red-600 text-sm">{errors.source_details}</p>}
                        </div>

                        <div className="mb-4">
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-gray-900 font-medium text-sm">Country Mappings</label>
                                <button
                                    type="button"
                                    onClick={addCountryMapping}
                                    className="bg-primary1 text-white py-1 px-2 text-sm rounded-lg"
                                >
                                    Add Country
                                </button>
                            </div>

                            {formData.future_original_country_mapping.map((mapping, index) => (
                                <div key={index} className="p-3 mb-3 border rounded-lg bg-gray-50">
                                    <div className="flex justify-between items-center mb-2">
                                        <h4 className="font-medium text-sm">Country Mapping #{index + 1}</h4>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => duplicateCountryMapping(index)}
                                                className="text-primary1 text-sm"
                                            >
                                                Duplicate
                                            </button>
                                            {formData.future_original_country_mapping.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeCountryMapping(index)}
                                                    className="text-red-600 text-sm"
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mb-2">
                                        <label className="block text-gray-900 font-medium text-sm mb-1">Country Code</label>
                                        <input
                                            type="text"
                                            placeholder="country code"
                                            className="block w-full border-gray-300 rounded-md shadow-sm px-4 py-2 bg-white"
                                            value={mapping.country_code}
                                            onChange={(e) => handleCountryMappingChange(index, 'country_code', e.target.value)}
                                        />
                                        {errors[`country_code_${index}`] && (
                                            <p className="text-red-600 text-sm">{errors[`country_code_${index}`]}</p>
                                        )}
                                    </div>

                                    {formData.is_series ? (
                                        <div className="mb-1">
                                            <div className="flex justify-between items-center mb-2">
                                                <label className="block text-gray-900 font-medium text-sm">Episode Release Dates</label>
                                                <button
                                                    type="button"
                                                    onClick={() => addEpisodeReleaseDate(index)}
                                                    className="bg-primary1 text-white py-1 px-2 text-xs rounded-lg"
                                                >
                                                    Add Date
                                                </button>
                                            </div>

                                            {mapping.episodes_release_date_time.length === 0 && (
                                                <div className="mb-2">
                                                    <p className="text-sm text-gray-600">No episode dates added yet</p>
                                                    <button
                                                        type="button"
                                                        onClick={() => addEpisodeReleaseDate(index)}
                                                        className="mt-1 text-primary1 text-sm underline"
                                                    >
                                                        Add your first episode date
                                                    </button>
                                                </div>
                                            )}

                                            {mapping.episodes_release_date_time.map((episodeDate, episodeIndex) => (
                                                <div
                                                    key={episodeIndex}
                                                    className="mb-2 flex items-center gap-2"
                                                    draggable
                                                    onDragStart={(e) => handleDragStart(e, index, episodeIndex)}
                                                    onDragOver={handleDragOver}
                                                    onDragLeave={handleDragLeave}
                                                    onDrop={(e) => handleDrop(e, index, episodeIndex)}
                                                    onDragEnd={handleDragEnd}
                                                >
                                                    <div className="flex-grow">
                                                        <div className="flex items-center mb-1">
                                                            <div className="flex items-center cursor-move">
                                                                <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" />
                                                                </svg>
                                                                <span className="text-xs text-gray-600">Episode #{episodeIndex + 1}</span>
                                                            </div>
                                                            {mapping.episodes_release_date_time.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeEpisodeReleaseDate(index, episodeIndex)}
                                                                    className="text-red-500 text-xs ml-2"
                                                                >
                                                                    Remove
                                                                </button>
                                                            )}
                                                        </div>
                                                        <input
                                                            type="datetime-local"
                                                            className="block w-full border-gray-300 rounded-md shadow-sm px-4 py-2 bg-white"
                                                            value={episodeDate || ''}
                                                            onChange={(e) => handleEpisodeDateChange(
                                                                index,
                                                                episodeIndex,
                                                                e.target.value
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                            ))}

                                            {errors[`episodes_release_date_time_${index}`] && (
                                                <p className="text-red-600 text-sm">{errors[`episodes_release_date_time_${index}`]}</p>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="mb-1">
                                            <label className="block text-gray-900 font-medium text-sm mb-1">Release Date</label>
                                            <input
                                                type="datetime-local"
                                                className="block w-full border-gray-300 rounded-md shadow-sm px-4 py-2 bg-white"
                                                value={mapping.release_date_time || ''}
                                                onChange={(e) => handleCountryMappingChange(
                                                    index,
                                                    'release_date_time',
                                                    e.target.value
                                                )}
                                            />
                                            {errors[`release_date_time_${index}`] && (
                                                <p className="text-red-600 text-sm">{errors[`release_date_time_${index}`]}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                    </div>
                    <div className="flex items-center justify-end pt-4 border-t">
                        <button type="submit" disabled={loading} className="bg-primary1 text-white py-2 px-3 rounded-lg">Add</button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default CreateFutureOriginal;
